import { HttpClient } from '@angular/common/http';
import { Logger } from './logger.service';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { tap, switchMap } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { User } from '../entity/user.entity';
import { UserApiService } from './user-api.service';

const LOCAL_STORAGE_TOKEN_KEY = 'access_token';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private readonly logger = new Logger(AuthService.name);

  public readonly authToken = new BehaviorSubject<string | null>(
    this.getToken()
  );
  public readonly authToken$ = this.authToken.asObservable();

  public readonly isAuthenticated = new BehaviorSubject<boolean>(
    !!this.getToken()
  );
  public readonly isAuthenticated$ = this.isAuthenticated.asObservable();

  private tokenCheckInterval: any;

  constructor(
    private http: HttpClient,
    private userApiService: UserApiService
  ) {
    this.isAuthenticated.next(!!this.getToken());

    // Check token validity every minute
    this.tokenCheckInterval = setInterval(() => {
      const token = this.getToken();
      if (!token) {
        this.logout();
      }
    }, 60000);
  }

  ngOnDestroy() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }
  }

  public getToken(): string | null {
    const token = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
    if (token && this.isTokenValid(token)) {
      return token;
    } else {
      return null;
    }
  }

  private isTokenValid(token: string): boolean {
    try {
      const decodedToken: any = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decodedToken.exp > currentTime;
    } catch (error) {
      this.logger.error('Error validating token', error);
      return false;
    }
  }

  public setToken(token: string): void {
    localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, token);
    this.authToken.next(token);
    this.isAuthenticated.next(true);
  }

  public login(email: string, password: string): Observable<User> {
    return this.http
      .post<{
        token: string;
      }>(`${environment.apiUrl}/api/login_check`, { username: email, password })
      .pipe(
        tap(response => {
          this.setToken(response.token);
        }),
        switchMap(() => this.userApiService.fetchCurrentUser())
      );
  }

  public logout(): void {
    this.logger.info('Logging out user');
    this.authToken.next(null);
    this.isAuthenticated.next(false);
    localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
    this.logger.debug('User logged out - token removed and auth state cleared');
  }
}
